import React, { useState, memo, useEffect } from 'react';
import styles from './styles.css';
import { useTranslation } from 'react-i18next';
import { LocationPickerMenu, SubjectPickerMenu, ViewPickerMenu } from '../../reusablePartials';
import Sticky from 'react-stickynode';
import { useAppContext } from '../../context/AppState.jsx';

export default function ExplorePanel({ title, dropdownButtons, navigationButton, errorMessage }) {
	const { t, i18n } = useTranslation();
	const [state, dispatch] = useAppContext();
	const show = state?.setMenuToOpen;

	// Detect mobile vs desktop
	const [isMobile, setIsMobile] = useState(false);
	const [activeTab, setActiveTab] = useState('measure'); // Track active tab separately from menu state

	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 640);
		};

		checkIsMobile();
		window.addEventListener('resize', checkIsMobile);

		return () => window.removeEventListener('resize', checkIsMobile);
	}, []);

	// Map icon paths to button indices
	const icons = ['/img/icons/measures_explore.svg', '/img/icons/locations_explore.svg', '/img/icons/views_explore.svg'];

	// Button type labels for the tabs
	const tabLabels = [t('Measure'), t('Location'), t('View')];
	const menuTypes = ['measure', 'location', 'view'];

	return (
		<div className={`explore ${styles.exploreWrapper}`}>
			<div className={styles.container}>
				<div className={styles.grid}>
					<div className={`${styles.fullWidth} ${styles.withPadding}`}>
						<p className={`explore-title ${styles.title}`}>{title}</p>
					</div>

					{!isMobile ? (
						// Desktop: Icons above each dropdown, all visible
						<>
							{dropdownButtons.map((button, index) => {
								const isActive = show === menuTypes[index];
								return (
									<div
										key={`desktop_${button.label.split(' ').join('')}_${index}`}
										className={`${styles.fullWidth} ${!navigationButton ? styles.desktopColumn3 : styles.desktopColumn4} ${styles.withPadding}`}
									>
										{/* Icon/Label Header - clickable on desktop */}
										<div className={styles.desktopTabHeader} onClick={() => button.onClick()}>
											<img src={icons[index]} alt="" className={styles.desktopIcon} />
											<span className={styles.desktopLabel}>{tabLabels[index]}</span>
										</div>

										{/* Dropdown Button */}
										<div className={styles.dropdownButtonWrapper}>
											<button
												onClick={() => button.onClick()}
												className={`menu-open-btn expand-selection-menu ${styles.dropdownButton} ${isActive ? styles.dropdownButtonActive : ''}`}
											>
												<span className="menu-title expand-selection-menu">{button.label}</span>
												<span className="expand-selection-menu" uk-icon="icon: chevron-down" />
											</button>
											{isActive && <div className={styles.dropdownPointer} />}
										</div>
									</div>
								);
							})}
						</>
					) : (
						// Mobile: Tab Navigation + Single Active Dropdown
						<>
							{/* Mobile: Tab Navigation */}
							<div className={styles.mobileTabNavigationWrapper}>
								{tabLabels.map((label, index) => {
									const isActive = activeTab === menuTypes[index];
									return (
										<div
											key={`mobile_tab_${label}`}
											className={`${styles.mobileTab} ${isActive ? styles.mobileTabActive : ''}`}
											onClick={() => {
												setActiveTab(menuTypes[index]);
												// Open the menu for the clicked tab
												dropdownButtons[index].onClick();
											}}
										>
											<img src={icons[index]} alt="" className={styles.mobileTabIcon} />
											<span className={styles.mobileTabLabel}>{label}</span>
										</div>
									);
								})}
							</div>

							{/* Mobile: Dropdown Selection Box - Only show for active tab */}
							{dropdownButtons.map((button, index) => {
								const isActiveTab = activeTab === menuTypes[index];
								if (!isActiveTab) return null;
								const isOpen = show === menuTypes[index];

								return (
									<div
										key={`mobile_${button.label.split(' ').join('')}_${index}`}
										className={`expand-selection-menu ${styles.mobileDropdownButton}`}
									>
										<div className={styles.dropdownButtonWrapper}>
											<button
												onClick={() => button.onClick()}
												className={`menu-open-btn expand-selection-menu ${styles.dropdownButton} ${isOpen ? styles.dropdownButtonActive : ''}`}
											>
												<span className="menu-title expand-selection-menu">{button.label}</span>
												<span className="expand-selection-menu" uk-icon="icon: chevron-down" />
											</button>
											{isOpen && <div className={styles.dropdownPointer} />}
										</div>
									</div>
								);
							})}
						</>
					)}
					{navigationButton ? (
						<div className={`${styles.fullWidth} ${styles.navigationButtonWrapper} ${styles.withPadding}`}>
							<button className="btn btn-block btn-outline-primary show-comparison" onClick={navigationButton.onClick}>
								{navigationButton.label}
							</button>
						</div>
					) : null}
				</div>

				<div className={navigationButton ? 'explore-with-go-button' : ''}>
					<SubjectPickerMenu />
					<LocationPickerMenu />
					<ViewPickerMenu />

					{show && errorMessage ? (
						<Sticky enabled={true} top={10} bottomBoundary={0} innerZ={140}>
							<div className={`${styles.exploreBarSelectionError}`}>
								<div className={`${styles.exploreBarSelectionErrorInner}`}>
									<p className={styles.errorText}>{errorMessage}</p>
								</div>
							</div>
						</Sticky>
					) : null}
				</div>
			</div>
		</div>
	);
}
